'use client';

import { RootState } from '@/redux/store';
import { useDispatch, useSelector } from 'react-redux';

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Typography,
} from '@mui/material';

import { hideModal } from '@/redux/navigationSlice';
import { MaterialIcon } from '@repo/ui';

type JsonObject = Record<string, unknown>;

const isObject = (value: unknown): value is JsonObject =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isArray = (value: unknown): value is unknown[] => Array.isArray(value);

const formatLabel = (key: string) =>
  key
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .replace(/^./, (s) => s.toUpperCase());

const NAME_KEY_PATTERN = /name|nombre|title|titulo/i;

const getItemLabel = (item: JsonObject, index: number) => {
  const nameEntry = Object.entries(item).find(
    ([key, value]) => NAME_KEY_PATTERN.test(key) && typeof value !== 'object' && value != null && value !== '',
  );

  return nameEntry ? String(nameEntry[1]) : `Item ${index + 1}`;
};

function Primitive({ value }: { value: unknown }) {
  if (typeof value === 'boolean') {
    return <Chip size="small" color={value ? 'success' : 'default'} label={value ? 'Yes' : 'No'} />;
  }

  if (value == null) {
    return (
      <Typography variant="body2" color="text.secondary" fontStyle="italic">
        No information extracted
      </Typography>
    );
  }

  return (
    <Typography
      variant="body2"
      sx={{
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
      }}
    >
      {String(value)}
    </Typography>
  );
}

function KeyValue({ label, value }: { label: string; value: unknown }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: '220px 1fr',
        },
        gap: 2,
        py: 1.25,
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Typography variant="body2" fontWeight={600} color="text.secondary">
        {label}
      </Typography>

      <Primitive value={value} />
    </Box>
  );
}

function Recursive({ value, title, depth = 0 }: { value: unknown; title?: string; depth?: number }) {
  let content: React.ReactNode;

  if (isArray(value)) {
    content = (
      <Stack spacing={2}>
        {value.map((item, index) => {
          const isRecord = isObject(item) && Object.keys(item).length > 1;

          if (!isRecord) {
            return <Recursive key={index} value={item} depth={depth + 1} />;
          }

          return (
            <Paper
              key={index}
              variant="outlined"
              sx={{
                borderRadius: 1,
                p: 2,
              }}
            >
              <Typography variant="overline" fontWeight={600} color="text.secondary" display="block" mb={1}>
                {getItemLabel(item, index)}
              </Typography>

              <Recursive value={item} depth={depth + 1} />
            </Paper>
          );
        })}
      </Stack>
    );
  } else if (isObject(value)) {
    content = (
      <Stack spacing={2}>
        {Object.entries(value).map(([key, child]) =>
          isObject(child) || isArray(child) ? (
            <Recursive key={key} title={formatLabel(key)} value={child} depth={depth + 1} />
          ) : (
            <KeyValue key={key} label={formatLabel(key)} value={child} />
          ),
        )}
      </Stack>
    );
  } else {
    content = <Primitive value={value} />;
  }

  if (!title) {
    return <>{content}</>;
  }

  const isTopLevel = depth === 0;

  return (
    <Accordion
      defaultExpanded
      disableGutters
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        '&:before': {
          display: 'none',
        },
      }}
    >
      <AccordionSummary
        expandIcon={<MaterialIcon icon="keyboard_arrow_down" size={isTopLevel ? 34 : 26} color="primary" />}
        sx={isTopLevel ? { bgcolor: 'action.hover' } : undefined}
      >
        <Typography variant={isTopLevel ? 'h6' : 'subtitle2'} fontWeight={isTopLevel ? 700 : 600}>
          {title}
        </Typography>
      </AccordionSummary>

      <AccordionDetails sx={{ pt: 2 }}>{content}</AccordionDetails>
    </Accordion>
  );
}

/**
 * Renders AI extracted document information.
 */
export const AIDataBody = (): React.JSX.Element | null => {
  const dispatch = useDispatch();
  const aiData = useSelector((state: RootState) => state.prospectData.aiDataToView) as unknown;

  if (!isObject(aiData)) {
    return null;
  }

  return (
    <Stack gap={4}>
      <Stack spacing={2}>
        {Object.entries(aiData).map(([key, value]) => (
          <Recursive key={key} title={formatLabel(key)} value={value} />
        ))}
      </Stack>
      <Stack direction="row" gap={2} width="100%" mt={2}>
        <Button fullWidth variant="outlined" onClick={() => dispatch(hideModal())}>
          Cerrar
        </Button>
      </Stack>
    </Stack>
  );
};
